// SubscriptionActions API — типизированные обёртки для продажи абонемента из карточки клиента.
// Своя api-прослойка под features/clients (НЕ трогаем features/subscriptions/* и features/cash/*).
// Контракт сверен с apps/api subscriptions.controller + cash-desk.controller:
//   GET  /api/v1/subscription-templates            → { data: SubscriptionTemplate[] }
//   GET  /api/v1/cash/shifts?status=open&limit=1    → { data: CashShiftListItem[] }
//   POST /api/v1/cash/sales                         → SaleDto (записывает Sale + платежи в кассу)
//
// Продажа теперь идёт через кассу: создаём Sale с минимум одной payment-allocation,
// сумма аллокаций обязана совпадать с total_amount_kopecks. Требуется открытая
// кассовая смена (cash_shift_id) под тем же филиалом, что и продажа.

import { api } from '@/lib/api';
import type { PaymentMethod, SubscriptionStatus, SubscriptionTemplateType } from '@/lib/contracts';

// Ре-экспорт единых контракт-типов (канон — @/lib/contracts).
export type { PaymentMethod, SubscriptionStatus, SubscriptionTemplateType };

export type SubscriptionValidityMode = 'rolling' | 'calendar_month';

/** Транспорт-форма шаблона абонемента (read). Зеркалит SubscriptionTemplateDto. */
export interface SubscriptionTemplate {
  id: string;
  name: string;
  description: string | null;
  type: SubscriptionTemplateType;
  price_kopecks: number;
  sessions_count: number | null;
  duration_days: number | null;
  validity_mode: SubscriptionValidityMode;
  price_per_session_kopecks: number | null;
  allow_debt: boolean;
  status: 'active' | 'archived';
  branch_ids: string[];
  group_ids: string[];
  created_at: string;
  updated_at: string;
}

/** Один entitlement в payload'е продажи. Зеркалит EntitlementInputSchema. */
export interface EntitlementInput {
  group_id: string | null;
  sessions_total: number | null;
  priority?: number;
}

/** Тело POST /api/v1/subscriptions. Зеркалит CreateSubscriptionSchema. */
export interface SellSubscriptionBody {
  client_id: string;
  template_id: string;
  branch_id: string;
  activated_at: string;
  expires_at?: string | null;
  price_at_sale_kopecks?: number;
  discount_amount_kopecks?: number;
  entitlements: EntitlementInput[];
  /**
   * Плательщик абонемента. null/отсутствует — клиент платит сам.
   * Для ребёнка (< 18) обычно указывают представителя.
   */
  payer_client_id?: string | null;
}

export interface SubscriptionEntitlement {
  id: string;
  group_id: string | null;
  sessions_total: number | null;
  sessions_remaining: number | null;
  priority: number;
}

/** Ответ POST /api/v1/subscriptions (SubscriptionDetailDto). */
export interface SubscriptionDetail {
  id: string;
  client_id: string;
  template_id: string;
  branch_id: string;
  status: SubscriptionStatus;
  price_at_sale_kopecks: number;
  discount_amount_kopecks: number;
  /** Фактически уплачено (net продажи) — Ф1 плана 39. */
  net_paid_kopecks: number;
  sold_at: string;
  activated_at: string;
  expires_at: string | null;
  sessions_total: number | null;
  sessions_remaining: number | null;
  entitlements: SubscriptionEntitlement[];
  active_freeze: {
    id: string;
    from_date: string;
    to_date: string;
    days: number;
    status: 'approved' | 'active';
  } | null;
}

/**
 * GET /api/v1/subscription-templates — прайс филиала продажи.
 *
 * `branchId` обязателен по смыслу, а не по типу: без него сервер отдаёт прайс
 * ВСЕЙ школы. Именно так московский менеджер видел в продаже 406 мурманских
 * позиций (жалоба владельца 13.08) — фильтр появился в плане 80 вместе с
 * кассой, а продажу из карточки клиента тогда не тронули.
 *
 * Правило сервера: у шаблона без единой привязки филиал не спрашивают — он
 * продаётся везде.
 */
export function listSubscriptionTemplates(
  branchId?: string,
): Promise<{ data: SubscriptionTemplate[] }> {
  const qs = branchId ? `?branch_id=${encodeURIComponent(branchId)}` : '';
  return api.get<{ data: SubscriptionTemplate[] }>(`/subscription-templates${qs}`);
}

/**
 * POST /api/v1/subscriptions — продажа (placeholder-flow без Sale, см. контроллер).
 * @deprecated Продажа теперь идёт через кассу — см. {@link createCashSale}. Оставлен для совместимости.
 */
export function sellSubscription(body: SellSubscriptionBody): Promise<SubscriptionDetail> {
  return api.post<SubscriptionDetail>('/subscriptions', body);
}

// ---------------------------------------------------------------------------
// Касса — открытая смена + продажа через Sale (записывает оплату в кассу).
// ---------------------------------------------------------------------------

// PaymentMethod — единый тип из @/lib/contracts (полный набор бэкенда).
// Модалка продажи абонемента показывает в селекте лишь подмножество методов
// (cash / bank_transfer / terminal / qr) — см. PAYMENT_METHODS в SellSubscriptionModal.

/** Одна аллокация платежа. Сумма всех аллокаций обязана равняться total_amount_kopecks. */
export interface PaymentAllocationInput {
  method: PaymentMethod;
  amount_kopecks: number;
  reference?: string | null;
}

/** Часть body про абонемент (внутри POST /cash/sales). */
export interface CashSaleSubscriptionInput {
  template_id: string;
  activated_at: string;
  entitlements: EntitlementInput[];
  /** Плательщик абонемента. null/отсутствует — клиент платит сам. */
  payer_client_id?: string | null;
  /**
   * «Да, продаём именно представителю» (план 138 Ф3). Без флага продажа
   * абонемента на карточку представителя, за которым числятся дети, отвечает
   * 422 `sale.representative_has_children` с их перечнем.
   */
  confirm_representative?: boolean;
  /** Группы, за которыми закреплён абонемент (план 101 §2.4). Пусто — любая. */
  covered_group_ids?: string[];
  /** План 121: сколько раз в неделю ходит; 1 — обязателен `visit_weekday`. */
  visits_per_week?: number;
  visit_weekday?: number;
}

/** Тело POST /api/v1/cash/sales. Зеркалит CreateSaleSchema (subscription-ветка). */
export interface CreateCashSaleBody {
  branch_id: string;
  client_id: string;
  /** Не нужна для полностью безналичной продажи — она кассу не затрагивает. */
  cash_shift_id?: string;
  /** «Архивная дата»: провести продажу задним числом (право sale:backdate). */
  sold_at?: string;
  subscription: CashSaleSubscriptionInput;
  total_amount_kopecks: number;
  discount_amount_kopecks: number;
  allocations: PaymentAllocationInput[];
  idempotency_key: string;
}

/** Открытая кассовая смена (минимум, который нужен модалке). Зеркалит CashShiftDto. */
interface CashShiftListItem {
  id: string;
  branch_id: string;
  status: 'open' | 'closed';
  opened_at: string;
}

/**
 * GET /api/v1/cash/shifts?status=open&limit=1 — ищет открытую смену.
 * Возвращает { id, branch_id } первой смены либо null, если открытых нет.
 *
 * `branchId` передавать ЯВНО, если важен конкретный филиал: без него сервер
 * вернёт первую смену из области видимости сотрудника, а со вторым городом
 * (план 80) это может оказаться смена соседнего филиала.
 */
export function listOpenShift(
  branchId?: string,
): Promise<{ id: string; branch_id: string } | null> {
  const qs = `?status=open&limit=1${branchId ? `&branch_id=${encodeURIComponent(branchId)}` : ''}`;
  return api.get<{ data: CashShiftListItem[] }>(`/cash/shifts${qs}`).then((res) => {
    const shift = res.data[0];
    return shift ? { id: shift.id, branch_id: shift.branch_id } : null;
  });
}

/**
 * Ответ POST /cash/sales. Сервер отдаёт полный SaleDto; здесь описаны поля,
 * которые нужны фронту. subscription_id используется, чтобы связать проданный
 * абонемент с уже созданной записью на занятие (карточка занятия, аренда).
 */
export interface CashSaleResult {
  id: string;
  subscription_id: string | null;
  /**
   * Итог автозаписи в группу (план 121 Ф3.2). null/нет поля — записи не было.
   * Пропуски по пересечению времени показываем администратору сразу: в базе он
   * их не увидит, а человек в эти дни на занятие не записан.
   */
  group_enrollment?: {
    enrolled: number;
    skipped_overlap: number;
    /**
     * Автозапись пропустила занятия, потому что абонемент оформлен на
     * представителя (план 138 A2). Продажу это не роняет: деньги уже приняты.
     * Поля нет в ответах прежней версии API — читать через `?? 0`.
     */
    skipped_representative?: number;
  } | null;
}

/**
 * POST /api/v1/cash/sales — продажа абонемента через кассу (Sale + платежи).
 * Idempotency-Key передаём и в header (требование api.ts для write-методов),
 * и в body (требование CreateSaleSchema) — оба из одного ключа.
 */
export function createCashSale(body: CreateCashSaleBody): Promise<CashSaleResult> {
  return api.post<CashSaleResult>('/cash/sales', body, {
    idempotencyKey: body.idempotency_key,
  });
}

/**
 * Расчёт срока, числа занятий и цены до подтверждения продажи.
 *
 * Нужен календарным абонементам Мурманска: срок у них — конец месяца активации,
 * число занятий считается из расписания выбранной группы, цена = цена за занятие
 * × число. До плана 101 эндпоинт существовал, но его не вызывал никто, и форма
 * продавала по базовой цене товара.
 */
export interface TemplateQuote {
  template_id: string;
  validity_mode: SubscriptionValidityMode;
  sessions_count: number | null;
  price_per_session_kopecks: number | null;
  total_price_kopecks: number;
  expires_at: string | null;
  /** Сколько занятий реально стоит в расписании; null — режим rolling. */
  occurrences_counted: number | null;
  /** Докуда заведено расписание группы: раньше конца месяца — счёт неполон. */
  schedule_generated_until: string | null;
  /** Дни расписания группы за месяц (план 121): из них выбирают день посещений. */
  schedule_weekdays: Array<{ weekday: number; time: string; count: number }>;
}

export function quoteTemplate(params: {
  templateId: string;
  branchId: string;
  activatedAt: string;
  groupIds?: string[];
  sessionsOverride?: number;
  /** План 121: считать только занятия этого дня недели (0=вс … 6=сб). */
  visitWeekday?: number;
}): Promise<TemplateQuote> {
  const q = new URLSearchParams({
    branch_id: params.branchId,
    activated_at: params.activatedAt,
  });
  if (params.groupIds && params.groupIds.length > 0) q.set('group_ids', params.groupIds.join(','));
  if (params.sessionsOverride !== undefined) {
    q.set('sessions_override', String(params.sessionsOverride));
  }
  if (params.visitWeekday !== undefined) q.set('visit_weekday', String(params.visitWeekday));
  return api.get<TemplateQuote>(`/subscription-templates/${params.templateId}/quote?${q}`);
}
